import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../api/axios';
import { io } from 'socket.io-client';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);
  const socketRef = React.useRef(null);

  const logout = React.useCallback(() => {
    localStorage.removeItem('amps_token');
    localStorage.removeItem('amps_refresh_token');
    localStorage.removeItem('amps_user');
    setUser(null);
    setRole(null);
    setIsAuthenticated(false);
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    setSocket(null);
  }, []);

  // Initialize auth state from token/user in localStorage
  useEffect(() => {
    const initAuth = async () => {
      const storedUser = localStorage.getItem('amps_user');
      const token = localStorage.getItem('amps_token');

      if (storedUser && token) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          setRole(parsedUser.role || 'USER');
          setIsAuthenticated(true);

          // Refresh user data from server to get latest modules/permissions
          const res = await api.get('/auth/me');
          if (res.data.success) {
            const freshUser = res.data.user;
            setUser(freshUser);
            localStorage.setItem('amps_user', JSON.stringify(freshUser));
          }
        } catch (error) {
          console.error("Failed to parse stored user or refresh", error);
          if (error.response?.status === 401) logout();
        }
      }
      setLoading(false);
    };
    
    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Socket Connection Management
  useEffect(() => {
    if (isAuthenticated && user) {
      const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:4000';
      const newSocket = io(SOCKET_URL, {
        path: '/socket.io',
        transports: ['websocket', 'polling'],
      });
      
      newSocket.on('connect', () => {
        console.log('✅ Connected to socket server');
        // Join a room based on userId for targeted updates
        newSocket.emit('join', user.id);
      });

      newSocket.on('PERMISSIONS_UPDATED', (data) => {
        console.log('🔄 Permissions updated via socket:', data);
        setUser(prev => {
          const updated = { ...prev, modules: data.modules };
          localStorage.setItem('amps_user', JSON.stringify(updated));
          return updated;
        });
      });

      socketRef.current = newSocket;
      setSocket(newSocket);

      return () => {
        newSocket.close();
        socketRef.current = null;
      };
    }
  }, [isAuthenticated, user]);

  const login = async (email, password) => {
    try {
      const res = await api.post('/auth/login', { email, password });
      
      // AGGRESSIVE EXTRACTION: Find the token wherever it hides
      const accessToken = res.data?.accessToken || res.data?.token || res.data?.data?.accessToken || res.data?.data?.token || res.data?.tokens?.accessToken;
      // (refresh token travels in the httpOnly cookie; nothing to store here)
      const userData = res.data?.user || res.data?.data?.user || res.data?.data;

      //[FIX] Restrict access: only non-USER, admin-level roles are permitted
      if (userData?.role === 'USER') {
        return { success: false, message: 'Access Denied: Admin, Centre, or Vendor privileges required.' };
      }

      if (!accessToken) {
          console.error("CRITICAL: Backend response did not contain an accessToken!", res.data);
          return { success: false, message: 'Server error: Session token missing' };
      }

      // STORE EXACT KEYS matching the interceptor.
      // SECURITY FIX (M3/H-1): only the SHORT-LIVED access token is persisted — the refresh
      // token lives in an httpOnly cookie set by the backend, out of reach of XSS.
      localStorage.setItem('amps_token', accessToken);
      localStorage.removeItem('amps_refresh_token');
      
      // UPDATE AXIOS DEFAULT IMMEDIATELY
      api.defaults.headers.common['Authorization'] = 'Bearer ' + accessToken;
      
      // Update Context State
      if (userData) {
        localStorage.setItem('amps_user', JSON.stringify(userData));
        setUser(userData);
        setRole(userData?.role || 'USER');
      }
      setIsAuthenticated(true);
      
      return { success: true };
    } catch (err) {
      console.error("Login failed:", err);
      return { 
        success: false, 
        message: err.response?.data?.message || 'Invalid credentials. Please try again.' 
      };
    }
  };

  return (
    <AuthContext.Provider value={{ user, role, isAuthenticated, loading, login, logout, socket }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
